import React, { FC, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Footer, FooterView, useMe } from 'src/common';
import { Header } from 'src/shared';
import { Paths } from 'src/pages/paths';
import { AccountSettings } from './Account/AccountSettings';
import NotificationsSettings from './Account/NotificationsSettings';
import CompanySettings from './Company/CompanySettings';
import EmployeeSettings from './Company/EmployeesSettings';
import styles from './index.module.scss';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import CompanySecurity from './Company/SecuritySettings';
import Webhooks from './Company/Webhooks';
import TokenSettings from './Company/IntegrationTokens';
import SettingsNav from './SettingsNav';
import CompetitorsSettings from './Company/CompetitorsSettings';
import CustomBidFields from './Company/CustomBidFields';
import { useApp, UserType } from 'src/models/auth';
import { Ownerships, ownership } from 'src/models/ownership';

export const SettingsPage: FC = () => {
  const { permission, authUser } = useApp();
  const { data: paidUser } = useMe();
  const isPaid = useMemo(() => permission === UserType.Paid && !!authUser, [authUser, permission]);
  const companyId = useMemo(() => (paidUser ? paidUser.company.id : null), [paidUser]);
  const isAdmin = useMemo(() => !!paidUser?.isAdmin, [paidUser]);
  const isAuthMfaFeature = useFeatureFlag(FeatureFlag.Auth_2FA);
  const isAuthSsoFeature = useFeatureFlag(FeatureFlag.Auth_SSO);
  const isWebhooksFeature = useFeatureFlag(FeatureFlag.Users_Webhooks);
  const isTokensFeature = useFeatureFlag(FeatureFlag.Users_IntegrationToken);
  const isCustomBidFieldsFeature = useFeatureFlag(FeatureFlag.CustomBidFields);

  const notificationsAvailable = ownership.name !== Ownerships.Clira;
  const employeesAvailable = !!companyId;
  const competitorsAvailable = ownership.name !== Ownerships.Clira;
  const webhooksAvailable = isWebhooksFeature && isAdmin;
  const tokensAvailable = isTokensFeature && isAdmin;
  const securityAvailable = (isAuthMfaFeature || isAuthSsoFeature) && isAdmin;

  return (
    <>
      <Header />
      <div className={styles.container}>
        <SettingsNav />
        <div className={styles.content}>
          <div className={styles.contentInner}>
            {isPaid ? (
              <Routes>
                {webhooksAvailable && <Route path={Paths.SETTINGS_PAGE_COMPANY_WEBHOOKS} element={<Webhooks />} />}
                {tokensAvailable && <Route path={Paths.SETTINGS_PAGE_COMPANY_TOKENS} element={<TokenSettings />} />}
                {isCustomBidFieldsFeature && (
                  <Route path={Paths.SETTINGS_PAGE_CUSTOM_BID_FIELDS} element={<CustomBidFields />} />
                )}
                {securityAvailable && (
                  <Route path={Paths.SETTINGS_PAGE_COMPANY_SECURITY} element={<CompanySecurity />} />
                )}
                {notificationsAvailable && (
                  <Route path={Paths.SETTINGS_PAGE_NOTIFICATIONS} element={<NotificationsSettings />} />
                )}

                {employeesAvailable && <Route path={Paths.SETTINGS_PAGE_EMPLOYEES} element={<EmployeeSettings />} />}
                {competitorsAvailable && (
                  <Route path={Paths.SETTINGS_PAGE_COMPETITORS} element={<CompetitorsSettings />} />
                )}
                <Route path={Paths.SETTINGS_PAGE_COMPANY_INFO} element={<CompanySettings />} />
                <Route path={Paths.SETTINGS_PAGE_ACCOUNT} element={<AccountSettings />} />
                <Route
                  path={`*`}
                  element={<Navigate to={`${Paths.SETTINGS_PAGE}${Paths.SETTINGS_PAGE_ACCOUNT}`} replace />}
                />
              </Routes>
            ) : (
              <Routes>
                <Route path={Paths.SETTINGS_PAGE_ACCOUNT} element={<AccountSettings />} />
                <Route
                  path={`*`}
                  element={<Navigate to={`${Paths.SETTINGS_PAGE}${Paths.SETTINGS_PAGE_ACCOUNT}`} replace />}
                />
              </Routes>
            )}
          </div>
          <Footer view={FooterView.logo} />
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
